import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["agentType", "betaDetails", "installUrl", "onEmbedClose", "onInstallComplete", "onInstallError", "slug", "sourceId"];
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useAiAccessControls } from 'ai-studio-lib/shared/hooks/queries/useAiAccessControls';
import { getAiSettingsUrl } from 'ai-studio-lib/shared/utils/urls';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import { useCallback, useMemo, useState } from 'react';
import { useCreateInstanceFromTemplateByGlobalIdMutation, useGetInstancesByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/ai/agent/rpc/AiAgentInstancesChirpServiceV2.hooks';
import { useInstallCustomAssistantTemplateFromGlobalIdMutation, useLookupUserInstalledAssistantByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/customassistants/rpc/CustomAssistantsService.hooks';
import { useSendInstallIntentLazyQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
import { useGetSatisfyingOptionsQuery } from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { useInstallCompatibility } from '../hooks/useInstallCompatibility';
import { JoinPublicBetaModal } from '../install/JoinPublicBetaModal';
import { getAgentStudioConfigurationUrl, getAgentStudioUrl, getBreezeStudioUrl } from '../utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../utils/TrackingUtils';
import { getAiAccessControlsTooltip } from '../utils/aiAccessControlsUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BREEZE_AGENT_EDIT = 'BREEZE_AGENT_EDIT';
const BREEZE_AGENT_ACCESS = 'BREEZE_AGENT_ACCESS';
function BreezeStudioLink({
  agentType,
  children
}) {
  return /*#__PURE__*/_jsx(Link, {
    external: true,
    href: getBreezeStudioUrl(agentType),
    children: children
  });
}
export function AgentCTAButton(_ref) {
  var _installCompatibility10;
  let {
      agentType,
      betaDetails,
      installUrl,
      onEmbedClose,
      onInstallComplete,
      onInstallError,
      slug,
      sourceId
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    appContext,
    listingId,
    name: listingName,
    published,
    productType,
    isEmbedded,
    trackInteraction,
    trackInstall,
    userInfo
  } = useDetailsPageContext();
  const [sendInstallIntent] = useSendInstallIntentLazyQuery();
  const {
    accessControls
  } = useAiAccessControls();
  const isPublicBeta = (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.betaStage) === 'PUBLIC_BETA';
  const isInboundPublicBeta = (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName) === 'AIAgent:AIStudio'; // Special case: all users will be ungated to AI Studio, any agent rollouts linked to this gate will not require a gate join or check
  const [isPublicBetaModalOpen, setIsPublicBetaModalOpen] = useState(false);
  const hasJoinedPublicBeta = useMemo(() => {
    if (isPublicBeta && betaDetails !== null && betaDetails !== void 0 && betaDetails.gateName) {
      var _userInfo$gates;
      return isInboundPublicBeta ? false : // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
      userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate(betaDetails.gateName));
    }
    return true;
  }, [isPublicBeta, betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName, isInboundPublicBeta, userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates]);
  const {
    data: installCompatibilityData,
    loading: installCompatibilityLoading
  } = useInstallCompatibility({
    listingId: listingId !== null && listingId !== void 0 ? listingId : 0,
    published,
    appContext
  });
  const launchCopilotWithCustomCopilot = useSendOnCopilotChannel('LAUNCH_WITH_CUSTOM_COPILOT');
  const {
    installed,
    missingScopes,
    userInstallable,
    portalInstallable
  } = useMemo(() => {
    var _installCompatibility, _installCompatibility2, _installCompatibility3, _installCompatibility4, _installCompatibility5, _installCompatibility6, _installCompatibility7, _installCompatibility8, _installCompatibility9;
    const installCompatibility = installCompatibilityData === null || installCompatibilityData === void 0 ? void 0 : installCompatibilityData.productInstallCompatibility;
    const missingUserScopes = (_installCompatibility = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility2 = installCompatibility.installability) === null || _installCompatibility2 === void 0 || (_installCompatibility2 = _installCompatibility2.uninstallableReason) === null || _installCompatibility2 === void 0 ? void 0 : _installCompatibility2.missingUserScopes) !== null && _installCompatibility !== void 0 ? _installCompatibility : [];
    const missingPortalScopes = (_installCompatibility3 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility4 = installCompatibility.installability) === null || _installCompatibility4 === void 0 || (_installCompatibility4 = _installCompatibility4.uninstallableReason) === null || _installCompatibility4 === void 0 ? void 0 : _installCompatibility4.missingPortalScopes) !== null && _installCompatibility3 !== void 0 ? _installCompatibility3 : [];
    const allMissingScopes = [...missingUserScopes.map(scope => scope.toLowerCase().replaceAll('_', '-')), ...missingPortalScopes.map(scope => scope.toLowerCase().replaceAll('_', '-'))];
    return {
      installed: (_installCompatibility5 = installCompatibility === null || installCompatibility === void 0 ? void 0 : installCompatibility.installed) !== null && _installCompatibility5 !== void 0 ? _installCompatibility5 : false,
      missingScopes: allMissingScopes,
      userInstallable: (_installCompatibility6 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility7 = installCompatibility.installability) === null || _installCompatibility7 === void 0 ? void 0 : _installCompatibility7.userInstallable) !== null && _installCompatibility6 !== void 0 ? _installCompatibility6 : true,
      //TODO: fallback to false once agent instance reliability is improved
      portalInstallable: (_installCompatibility8 = installCompatibility === null || installCompatibility === void 0 || (_installCompatibility9 = installCompatibility.installability) === null || _installCompatibility9 === void 0 ? void 0 : _installCompatibility9.portalInstallable) !== null && _installCompatibility8 !== void 0 ? _installCompatibility8 : true //TODO: fallback to false once agent instance reliability is improved
    };
  }, [installCompatibilityData]);
  const {
    data: missingPlanData
  } = useGetSatisfyingOptionsQuery({
    variables: {
      scopes: missingScopes
    },
    skip: missingScopes.length === 0
  });
  const uninstallableReason = installCompatibilityData === null || installCompatibilityData === void 0 || (_installCompatibility10 = installCompatibilityData.productInstallCompatibility) === null || _installCompatibility10 === void 0 || (_installCompatibility10 = _installCompatibility10.installability) === null || _installCompatibility10 === void 0 ? void 0 : _installCompatibility10.uninstallableReason;
  const missingBreezeAgentAccess = uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.missingUserScopes.includes(BREEZE_AGENT_ACCESS);
  const OpenAISettingsLink = useMemo(() => ({
    children
  }) => {
    return /*#__PURE__*/_jsx(Link, {
      external: true,
      href: getAiSettingsUrl(),
      onClick: () => {
        trackInteraction({
          action: 'click ai settings link'
        });
      },
      use: "on-dark",
      whiteSpace: "nowrap",
      children: children
    });
  }, [trackInteraction]);

  // Check if user has required access controls
  const aiAccessControlsTooltip = useMemo(() => getAiAccessControlsTooltip(accessControls, agentType, OpenAISettingsLink, sourceId), [sourceId, accessControls, agentType, OpenAISettingsLink]);

  // Prepare tooltip content for disabled button
  const tooltipContent = useMemo(() => {
    // Check for AI access controls first
    if (aiAccessControlsTooltip) {
      return aiAccessControlsTooltip;
    }

    // Default to uninstallable reason text
    if (uninstallableReason !== null && uninstallableReason !== void 0 && uninstallableReason.missingUserScopes.includes(BREEZE_AGENT_EDIT) || missingBreezeAgentAccess) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "detailsPage.productDetailsCard.cta.uninstallableReasons.breezeAgentEdit"
      });
    }
    const missingPlanOptions = missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions;
    if (missingPlanOptions && missingPlanOptions.length === 1 && missingPlanOptions[0].and.length === 1) {
      if (missingPlanOptions[0].and[0].or.length === 1) {
        const option = missingPlanOptions[0].and[0].or[0];
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlan",
          options: {
            planName: option.displayName,
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        });
      }
      return /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlans",
          options: {
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        }), /*#__PURE__*/_jsx(List, {
          marginBlockStart: "200",
          marginInlineStart: "-400",
          styled: true,
          children: missingPlanOptions[0].and[0].or.map((option, index) => /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.uninstallableReasons.orHigherTier",
              options: {
                planName: option.displayName
              }
            })
          }, index))
        })]
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.uninstallableReasons.cannotInstallAgent"
    });
  }, [aiAccessControlsTooltip, uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.missingUserScopes, missingBreezeAgentAccess, missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions, productType]);
  const {
    data: agentInstancesData,
    loading: agentInstanceLoading,
    refetch: agentInstancesRefetch
  } = useGetInstancesByGlobalIdQuery({
    skip: !sourceId || appContext !== 'app' || agentType !== 'STUDIO',
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const agentInstance = useMemo(() => {
    if (!agentInstancesData || agentInstancesData.instances.length === 0) {
      return undefined;
    }
    return agentInstancesData.instances[0];
  }, [agentInstancesData]);

  /**
   * Determines the href for the agent CTA button based on agent type and instance data
   */
  const getAgentOpenButtonHref = useCallback(() => {
    switch (agentType) {
      case 'OFF_PLATFORM':
        return installUrl;
      case 'STUDIO':
        return agentInstance !== null && agentInstance !== void 0 && agentInstance.id ? getAgentStudioUrl(agentInstance.id) : getBreezeStudioUrl(agentType);
      case 'ASSISTANT':
        return undefined;
      // No href, handled by onClick
      default:
        return undefined;
    }
  }, [agentType, agentInstance === null || agentInstance === void 0 ? void 0 : agentInstance.id, installUrl]);
  const {
    data: installedAssistantData,
    refetch: installedAssistantRefetch,
    loading: installedAssistantLoading
  } = useLookupUserInstalledAssistantByGlobalIdQuery({
    variables: {
      globalId: sourceId || ''
    },
    skip: !sourceId || appContext !== 'app' || agentType !== 'ASSISTANT'
  });
  const installedAssistant = useMemo(() => {
    if (!installedAssistantData) {
      return undefined;
    }
    return installedAssistantData.installedAssistant;
  }, [installedAssistantData]);
  const [createAgentInstance, {
    loading: createAgentInstanceLoading
  }] = useCreateInstanceFromTemplateByGlobalIdMutation({
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const [installAssistantFromGlobalId, {
    loading: installAssistantFromGlobalIdLoading
  }] = useInstallCustomAssistantTemplateFromGlobalIdMutation({
    variables: {
      templateGlobalId: sourceId || ''
    }
  });
  const handleAgentInstalledSuccessfully = useCallback(instanceId => {
    if (agentType === 'ASSISTANT') {
      // Refetch assistant install data after successful install
      installedAssistantRefetch().catch(error => {
        console.error(error);
      });
    }
    FloatingAlertStore.addAlert({
      maxInlineSize: 'unset',
      sticky: true,
      titleText: listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledSuccessAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          linkProps: {
            agentType
          },
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledSuccessAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          linkProps: {
            agentType
          }
        }
      }),
      type: 'success',
      message: instanceId && /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, Object.assign({
          href: getAgentStudioConfigurationUrl(instanceId, agentType),
          onClick: () => trackInteraction({
            action: 'clicked open cta button',
            subscreen: 'install success alert'
          }),
          external: true,
          size: "small",
          use: "tertiary"
        }, rest, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.configure"
          })
        }))
      })
    });
    agentInstancesRefetch().catch(error => {
      console.error('Error refetching agent data:', error);
    });
    if (onInstallComplete) {
      onInstallComplete();
    }
  }, [agentInstancesRefetch, agentType, installedAssistantRefetch, listingName, onInstallComplete, rest, trackInteraction]);
  const handleAgentInstallFailed = useCallback(error => {
    console.error('Error installing agent:', error);
    FloatingAlertStore.addAlert({
      maxInlineSize: 'unset',
      titleText: listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledFailedAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledFailedAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        }
      }),
      type: 'danger'
    });
    if (onInstallError) {
      onInstallError();
    }
  }, [listingName, onInstallError]);
  const handleInstallAgent = () => {
    if (agentType === 'ASSISTANT') {
      installAssistantFromGlobalId().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result;
          handleAgentInstalledSuccessfully((_response$data$result = response.data.result) === null || _response$data$result === void 0 || (_response$data$result = _response$data$result.customAssistant) === null || _response$data$result === void 0 ? void 0 : _response$data$result.id);
        }
      }).catch(error => handleAgentInstallFailed(error));
    } else {
      createAgentInstance().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result2;
          handleAgentInstalledSuccessfully((_response$data$result2 = response.data.result) === null || _response$data$result2 === void 0 || (_response$data$result2 = _response$data$result2.agentInstance) === null || _response$data$result2 === void 0 ? void 0 : _response$data$result2.id);
        }
      }).catch(error => {
        handleAgentInstallFailed(error);
      });
    }
  };
  const handlePostBetaJoin = () => {
    if (agentType === 'OFF_PLATFORM' && installUrl) {
      window.open(installUrl, '_blank', 'noopener');
    } else {
      handleInstallAgent();
    }
  };

  // Render a loading state button to prevent vulnerabilities that can be caused by assuming that a user can install
  if (createAgentInstanceLoading || installedAssistantLoading || installCompatibilityLoading || agentInstanceLoading || installAssistantFromGlobalIdLoading) {
    return /*#__PURE__*/_jsx(LoadingButton, {
      size: "medium",
      use: "primary",
      disabled: true,
      loading: true,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.AGENT"
      })
    });
  }

  // Render an "Open" button if the agent is installed except for when the user does not have agent studio access
  if ((installed || agentInstance !== null && agentInstance !== void 0 && agentInstance.id || installedAssistant !== null && installedAssistant !== void 0 && installedAssistant.id) && !missingBreezeAgentAccess) {
    return (
      /*#__PURE__*/
      // TODO: some handling might be needed for an agent that does not have a studio URL (Customer Agent)
      _jsx(Button, Object.assign({
        use: "primary",
        href: getAgentOpenButtonHref(),
        external: isEmbedded,
        onClick: () => {
          trackInteraction({
            action: 'clicked open cta button'
          });
          if (agentType === 'ASSISTANT') {
            launchCopilotWithCustomCopilot({
              customAssistantType: 'chatAgent',
              customAssistant: {
                id: installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.id,
                name: (installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.name) || '',
                welcomeMessage: installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.description,
                iconUrl: installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.icon,
                type: 'chatAgent'
              }
            });
            if (onEmbedClose) {
              onEmbedClose();
            }
          }
        }
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.open"
        })
      }))
    );
  }
  // Render a disabled button if the agent is not installable
  if (!userInstallable || !portalInstallable || aiAccessControlsTooltip !== null) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipContent,
      minInlineSize: "240px",
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        disabled: true
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.AGENT"
        })
      }))
    });
  }

  // Render a "Join beta and add" button if the agent is installable but the user has not joined the public beta
  if (isPublicBeta && !hasJoinedPublicBeta) {
    var _betaDetails$rolloutI;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        onClick: () => {
          void sendInstallIntent({
            variables: {
              slug
            }
          });
          trackInteraction({
            action: 'clicked join beta and add cta button'
          });
          setIsPublicBetaModalOpen(true);
        }
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.joinBetaAndAdd.${agentType === 'ASSISTANT' ? 'ASSISTANT' : 'AGENT'}`
        })
      })), isPublicBetaModalOpen && /*#__PURE__*/_jsx(JoinPublicBetaModal, {
        agentType: agentType || '',
        title: listingName,
        onClose: () => setIsPublicBetaModalOpen(false),
        rolloutId: (_betaDetails$rolloutI = betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.rolloutId) !== null && _betaDetails$rolloutI !== void 0 ? _betaDetails$rolloutI : 0,
        onSuccessfulJoin: handlePostBetaJoin,
        skipJoin: isInboundPublicBeta
      })]
    });
  }

  // Render an external link button for off-platform agents
  if (agentType === 'OFF_PLATFORM') {
    return /*#__PURE__*/_jsx(Button, Object.assign({
      href: installUrl !== null && installUrl !== void 0 ? installUrl : '',
      external: isEmbedded,
      onClick: () => {
        void sendInstallIntent({
          variables: {
            slug
          }
        });
        trackInteraction({
          action: 'clicked off platform Install button'
        });
        trackInstall({});
      }
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.AGENT"
      })
    }));
  }

  // Render standard button for all other agents
  return /*#__PURE__*/_jsx(Button, Object.assign({
    use: "primary",
    onClick: () => {
      void sendInstallIntent({
        variables: {
          slug
        }
      });
      trackInstall({});
      trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
      handleInstallAgent();
    }
  }, rest, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.install.AGENT"
    })
  }));
}